import { useEffect, useRef } from "react";

import {
    type IntersectionOptions,
    type InViewHookResponse,
    useInView
    // @ts-ignore
} from "react-intersection-observer";

import {
    type Experiment,
    type ExperimentEnrollmentStatus,
    useExperiment
} from "./use-experiment";

type ExperimentOnView<T extends Experiment<unknown>> = {
    experiment: ExperimentEnrollmentStatus<T["defaultValue"]>;
    inViewHook: InViewHookResponse;
};

/**
 * Hook that fires enrollment on view for a given experiment manifest. It will fire the exposure event when the element is in view.
 * @param experiment The experiment manifest
 * @param observerOptions The observer options for the intersection observer, default values applied { threshold: 0.1, triggerOnce: true }
 * @param metadata The metadata for tracking events
 * @returns {ExperimentEnrollmentStatus}
 */
export function useExperimentOnView<T extends Experiment<unknown>>(
    experiment: T,
    {
        observerOptions,
        metadata
    }: {
        observerOptions?: IntersectionOptions;
        metadata?: object;
    } = {}
): ExperimentOnView<T> {
    const inViewHook = useInView({
        threshold: 0.1,
        triggerOnce: true,
        ...observerOptions
    });
    const experimentManualInView = useExperimentManualInView(
        experiment,
        inViewHook.inView,
        metadata
    );

    return {
        experiment: experimentManualInView,
        inViewHook
    };
}

/**
 * Hook that fires enrollment on view for a given experiment manifest. It will fire the exposure event when inView property has been true.
 * @param experiment The experiment manifest
 * @param inView manually indicate if the element is in view
 * @param metadata The metadata for tracking events
 * @returns {ExperimentEnrollmentStatus}
 */
export function useExperimentManualInView<T extends Experiment<unknown>>(
    experiment: T,
    inView: boolean,
    metadata?: object
): ExperimentEnrollmentStatus<T["defaultValue"]> {
    const hasFired = useRef(false);
    const experimentHook = useExperiment<T>(
        {
            ...experiment,
            options: {
                ...experiment.options,
                fireExperimentExposure: false
            }
        },
        metadata
    );

    useEffect(() => {
        if (hasFired.current) {
            return;
        }
        if (inView && experimentHook.type === "enrolled") {
            experimentHook.fireExposure();
            hasFired.current = true;
        }
    }, [experimentHook, inView]);

    return experimentHook;
}
